import type { TreeNode } from '@morph-mapper/types';
import type { FieldSuggestion } from '../types';
import { isNumericIntentField } from '../definitions/number-handling';

/** What the field tells about the value it expects. */
export type FieldExpectation = {
  /** The field holds a number. */
  numeric?: boolean;
  /** The field only accepts certain values (e.g. an enum). */
  accepts?: (value: string) => boolean;
};

/** The values an enum field accepts, from its zod validation. */
const enumValues = (validation: unknown): string[] | null => {
  const def = (validation as { _def?: { typeName?: string; values?: unknown } } | undefined)?._def;
  return def?.typeName === 'ZodEnum' && Array.isArray(def.values) ? (def.values as string[]) : null;
};

/** What a field entry tells about its expected value. */
export const fieldExpectation = (
  entry: Pick<TreeNode, 'key' | 'validation' | 'semantics'>,
): FieldExpectation => {
  const values = enumValues(entry.validation);
  return {
    numeric: isNumericIntentField(entry),
    accepts: values ? (value) => values.includes(value) : undefined,
  };
};

// Declare Items prefixes a table value with its column ("c6: PU-4471"); the
// value after it is what confirms.
const COLUMN_PREFIX = /^c\d+:\s*/i;

/** A value as text that can confirm, or null when it isn't text. */
const confirmingText = (value: unknown) =>
  typeof value === 'string' ? value.trim().replace(COLUMN_PREFIX, '') : null;

/**
 * Whether something other than the plugin's own guess vouches for a value
 * derived from a suggestion (a part of it, a cleaned version): the value the
 * plugin expected (e.g. the LLM's reading), another suggestion with that
 * value, or a field that only accepts certain values and accepts this one
 * while it doesn't accept the suggestion as it is. `same` decides when two
 * texts are the same value; exact by default.
 */
export const confirmation = (
  suggestion: FieldSuggestion,
  others: FieldSuggestion[],
  field: FieldExpectation,
  whole: string,
  same: (a: string, b: string) => boolean = (a, b) => a === b,
) => {
  const matches = (value: unknown, candidate: string) => {
    const t = confirmingText(value);
    return t !== null && same(t, candidate);
  };
  const fieldRejectsWhole = field.accepts !== undefined && !field.accepts(whole);
  return (candidate: string) =>
    matches(suggestion.expectedValue, candidate) ||
    others.some(
      (s) =>
        s !== suggestion && (matches(s.value, candidate) || matches(s.expectedValue, candidate)),
    ) ||
    (fieldRejectsWhole && field.accepts!(candidate));
};

/**
 * The texts that could confirm a value derived from a suggestion, as
 * `confirmation` checks them: the value the plugin expected, other
 * suggestions' values and expected values, and the values an enum field
 * accepts when it doesn't accept the suggestion as it is. For plugins that
 * need the target text itself, e.g. to find where it sits in the value.
 */
const confirmingTexts = (
  suggestion: FieldSuggestion,
  others: FieldSuggestion[],
  field: FieldExpectation,
  whole: string,
  enumValues: string[] = [],
): string[] => {
  const text = (value: unknown) => confirmingText(value) ?? '';
  const fieldRejectsWhole = field.accepts !== undefined && !field.accepts(whole);
  const texts = [
    text(suggestion.expectedValue),
    ...others
      .filter((s) => s !== suggestion)
      .flatMap((s) => [text(s.value), text(s.expectedValue)]),
    ...(fieldRejectsWhole ? enumValues.filter((v) => field.accepts!(v)) : []),
  ];
  return [...new Set(texts)].filter((t) => t !== '' && t !== text(whole));
};

/**
 * The confirming texts for a text suggestion as a transform step sees it
 * (see confirmingTexts); none for other values and iterator suggestions.
 */
export const confirmingTextsFor = (
  state: { value: unknown },
  suggestion: FieldSuggestion,
  others: FieldSuggestion[],
  field: FieldExpectation,
  enumValues: string[] = [],
): string[] =>
  typeof state.value === 'string' && suggestion.iterator !== true
    ? confirmingTexts(suggestion, others, field, state.value, enumValues)
    : [];

/** The values an enum field accepts, or none. */
export const acceptedValues = (entry: Pick<TreeNode, 'validation'>): string[] =>
  enumValues(entry.validation) ?? [];

/** Compares texts ignoring how spaces and line breaks are laid out. */
export const sameIgnoringSpaces = (a: string, b: string) =>
  a.replace(/\s+/g, ' ').trim() === b.replace(/\s+/g, ' ').trim();
